import React from 'react';
import Link from 'next/link.js';
import { styled } from '@components-shared/src/stitches';
import { CoverImages } from '@components/Playlists/basics/CoverImages';
import { Avatar } from '@components/basics/Images/Avatar';
import { Cover } from '@components/basics/Images/Cover';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import type {
  SearchResultArtistFragment,
  SearchResultCollectorFragment,
  SearchResultReleaseFragment,
  SearchResultShelfFragment,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { addSearchHistory } from './SearchHistory/useSearchHistoryStore';

export const SearchResultRow = styled('a', {
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  gap: '.75rem',
  minHeight: '0.1rem',
  paddingTop: '0.355rem',
  paddingBottom: '0.355rem',

  paddingLeft: '1.125rem',
  paddingRight: '0.375rem',

  fontSize: '.75rem',
  color: 'black',
  fontWeight: '500',
  '&:before': {
    borderRadius: '$sm',
    margin: '0 0.65rem',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 'stretch',
    height: '100%',
    display: 'block',
    content: '',
    zIndex: '$below',
  },
  '&:hover:before': {
    background: '$neutral100',
  },
});

export const ImageContainer = styled('div', {
  position: 'relative',
  height: '40px',
  width: '40px',
  overflow: 'hidden',
  flexShrink: 0,
  borderRadius: 6,

  '-webkit-transform': 'translateZ(0)',

  variants: {
    overflow: {
      hidden: {
        overflow: 'hidden',
      },
    },
    imgBorderRadius: {
      sm: {
        img: {
          borderRadius: '$sm',
        },
      },
    },
  },
});

const ArtistNameContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '4px',
  alignItems: 'center',
});

export const SearchResultTextContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  maxWidth: '45em',
  gap: '2px',
  overflow: 'hidden',
});

export const SearchResultText = styled('span', {
  fontFamily: '$base',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  color: '$neutral800',
  lineHeight: '20px',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

export const SearchResultSubText = styled('span', {
  fontFamily: '$base',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '12px',
  color: '$neutral500',
  lineHeight: '14px',
  maxWidth: 'fit-content',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  display: 'block',
  textOverflow: 'ellipsis',
});

const COVER_SIZE = 40;

const subTextSeparator = ' • ';

export function PlaylistRow({
  playlist,
  subtitle,
  onRowClick,
}: {
  playlist: SearchResultShelfFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const playlistName = playlist.name;

  const username = playlist.user.username;
  const isArtist = !!playlist.user.artist;

  return (
    <Link href={webappUriPath(playlist.webappUri)} key={playlist.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: playlist.id,
            name: playlistName,
            subText: username,
            type: 'Playlist',
          });
        }}
      >
        <ImageContainer overflow="hidden">
          <CoverImages playlist={playlist} maxSize={COVER_SIZE} priority={false} />
        </ImageContainer>

        <SearchResultTextContainer>
          <SearchResultText>{playlistName}</SearchResultText>
          {isArtist ? (
            <ArtistNameContainer>
              <SearchResultSubText>
                {subtitle ? `${subtitle}${subTextSeparator}${username}` : username}
              </SearchResultSubText>
              <VerifiedTooltip size={13.5} />
            </ArtistNameContainer>
          ) : (
            <SearchResultSubText>
              {subtitle ? `${subtitle}${subTextSeparator}${username}` : username}
            </SearchResultSubText>
          )}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

export function ReleaseRow({
  release,
  subtitle,
  onRowClick,
}: {
  release: SearchResultReleaseFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const title = release.title;

  const artistName = release.artist.name || release.artist.soundHandle || null;

  return (
    <Link href={webappUriPath(release.webappUri)} key={release.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: release.id,
            type: 'Release',
            name: title,
            subText: artistName,
          });
        }}
      >
        <ImageContainer>
          <Cover
            size={40}
            src={release.staticCoverImage?.url || release.coverImage.url}
            alt={release.title}
            borderRadius={6}
          />
        </ImageContainer>
        <SearchResultTextContainer>
          <SearchResultText>{title}</SearchResultText>
          {subtitle ? (
            <SearchResultSubText>
              {artistName ? `${subtitle}${subTextSeparator}${artistName}` : subtitle}
            </SearchResultSubText>
          ) : artistName ? (
            <SearchResultSubText>{artistName}</SearchResultSubText>
          ) : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

export function ArtistRow({
  artist,
  subtitle,
  onRowClick,
}: {
  artist: SearchResultArtistFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  if (!artist.name) return null;

  const name = artist.name;

  return (
    <Link href={webappUriPath(artist.webappUri)} key={artist.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: artist.id,
            name,
            type: 'Artist',
            subText: null,
          });
        }}
      >
        <Avatar
          size={COVER_SIZE}
          src={artist.user.avatar?.url}
          walletAddress={artist.user.publicAddress}
          disableLink
          borderRadius="50%"
          webappUri={artist.webappUri}
        />
        <SearchResultTextContainer>
          <ArtistNameContainer>
            <SearchResultText>{name}</SearchResultText>
            <VerifiedTooltip size={18} />
          </ArtistNameContainer>
          {subtitle ? <SearchResultSubText>{subtitle}</SearchResultSubText> : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}

export function CollectorRow({
  collector,
  subtitle,
  onRowClick,
}: {
  collector: SearchResultCollectorFragment;
  subtitle?: string;
  onRowClick: () => void;
}) {
  const username = collector.username;
  return (
    <Link href={webappUriPath(collector.webappUri)} key={collector.id} passHref>
      <SearchResultRow
        onClick={() => {
          onRowClick();
          addSearchHistory({
            id: collector.id,
            name: collector.username,
            type: 'Collector',
            subText: null,
          });
        }}
      >
        <Avatar
          size={COVER_SIZE}
          src={collector.avatar?.url}
          walletAddress={collector.publicAddress}
          borderRadius="50%"
          webappUri={collector.webappUri}
        />
        <SearchResultTextContainer>
          <SearchResultText>{username}</SearchResultText>
          {subtitle ? <SearchResultSubText>{subtitle}</SearchResultSubText> : null}
        </SearchResultTextContainer>
      </SearchResultRow>
    </Link>
  );
}
